import { type Event, NULL_TIME, type Property, QuoteDocument, type User } from '@kalos/kalos-rpc';
import { addDays, format } from 'date-fns';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import {
  useCreateQuoteDocumentMutation,
  useGetQuoteDocumentQuery,
} from '../../../hooks/react-query/useQuoteDocumentQuery';
import { useQuoteLineQuery } from '../../../hooks/react-query/useQuoteLineQuery';
import {
  getQuoteDocumentTaxCost,
  getQuoteDocumentTaxRate,
} from '../../../utils/quoteDocumentPricing';
import {
  ProposalContext,
  type ProposalContextType,
  type ProposalData,
  type ProposalStatus,
} from './ProposalContext';

interface ProposalProviderProps {
  children: ReactNode;
  eventId: number;
  event?: Event | null;
  customer?: User | null;
  property?: Property | null;
}

export const ProposalProvider: FC<ProposalProviderProps> = ({
  children,
  eventId,
  event = null,
  customer = null,
  property = null,
}) => {
  const [error, setError] = useState<string | null>(null);
  const loggedUser = useAuth();

  // Fetch quote document
  const {
    data: quoteDocument,
    isLoading: isQuoteDocumentLoading,
    refetch: refetchQuoteDocument,
  } = useGetQuoteDocumentQuery({
    eventId,
  });

  const createQuoteDocumentMutation = useCreateQuoteDocumentMutation();

  // Ensure quote document exists: create if none. Loading stays true until we have a doc.
  useEffect(() => {
    if (
      !isQuoteDocumentLoading &&
      quoteDocument === null &&
      eventId &&
      event?.propertyId &&
      !createQuoteDocumentMutation.isPending
    ) {
      const now = new Date();
      const thirtyDaysFromNow = format(addDays(now, 30), 'yyyy-MM-dd HH:mm:ss');
      createQuoteDocumentMutation.mutate(
        QuoteDocument.create({
          eventId,
          propertyId: event.propertyId,
          jobNotes: '',
          preparedById: loggedUser.user.id || 0,
          displayBusinessName: 0,
          includeJobNotes: 0,
          includeAttachments: 1,
          blockLineItems: 0,
          validUntilDate: thirtyDaysFromNow,
          quoteExpireDate: thirtyDaysFromNow,
          discount: event.discount ?? '0',
          discountcost: event.discountcost ?? '0',
          tax: parseFloat(String(event.tax ?? 0)) || 0,
          taxCost: parseFloat(String(event.taxCost ?? 0)) || 0,
        }),
      );
    }
  }, [
    isQuoteDocumentLoading,
    quoteDocument,
    eventId,
    event?.propertyId,
    event?.discount,
    event?.discountcost,
    event?.tax,
    event?.taxCost,
    createQuoteDocumentMutation.isPending,
    loggedUser.user.id,
    createQuoteDocumentMutation,
  ]);

  // Fetch quote lines: scope by quoteDocumentId when doc exists (align with Proposal main)
  const {
    data: quoteLineData,
    isLoading: isQuoteLinesLoading,
    refetch: refetchQuoteLines,
  } = useQuoteLineQuery({
    filters: {
      isActive: 1,
      ...(quoteDocument?.id
        ? { quoteDocumentId: quoteDocument.id }
        : { jobNumber: eventId.toString() }),
      withoutLimit: true,
    },
  });

  // Determine proposal status
  const proposalStatus: ProposalStatus = useMemo(() => {
    if (!quoteDocument) return 'draft';
    if (quoteDocument.signedDate && quoteDocument.signedDate !== NULL_TIME) {
      return 'signed';
    }
    if (quoteDocument.quoteSentDate && quoteDocument.quoteSentDate !== NULL_TIME) {
      return 'sent';
    }
    return 'draft';
  }, [quoteDocument]);

  // Centralized proposal calculations (same logic as ProposalPreview)
  const proposalData = useMemo((): ProposalData => {
    const quoteLines = quoteLineData?.results || [];

    // Calculate subtotal from quote lines
    const subtotal = quoteLines.reduce((sum, line) => {
      const adjustment = line.adjustment || '0';
      // Remove any $ and commas before parsing
      const cleanedValue = adjustment.replace(/[$,]/g, '');
      const price = parseFloat(cleanedValue);
      const quantity = line.quotedQuantity || 1;
      return sum + (isNaN(price) ? 0 : price * quantity);
    }, 0);

    const doc = quoteDocument;
    const discountPercent = parseFloat((doc?.id ? doc.discount : event?.discount) || '0');
    const discountCostValue = parseFloat((doc?.id ? doc.discountcost : event?.discountcost) || '0');
    const excludedSubtotal = quoteLines.reduce((sum, line) => {
      if (line.excludeInvoiceDiscount !== 1) return sum;
      const adjustment = line.adjustment || '0';
      const cleanedValue = adjustment.replace(/[$,]/g, '');
      const price = parseFloat(cleanedValue);
      const quantity = line.quotedQuantity || 1;
      return sum + (isNaN(price) ? 0 : price * quantity);
    }, 0);
    const discountableBase = subtotal - excludedSubtotal;
    const discountAmount =
      discountPercent > 0 ? discountableBase * (discountPercent / 100) : discountCostValue;

    const afterDiscount = subtotal - discountAmount;
    const taxRate = doc?.id ? getQuoteDocumentTaxRate(doc) : 0;
    const taxCostFlat = doc?.id ? getQuoteDocumentTaxCost(doc) : 0;
    const taxAmount = taxRate > 0 ? afterDiscount * (taxRate / 100) : taxCostFlat;
    const total = afterDiscount + taxAmount;

    return {
      quoteLines,
      subtotal,
      discountPercent,
      discountAmount,
      taxRate,
      taxAmount,
      total,
      status: proposalStatus,
      lineItemCount: quoteLines.length,
    };
  }, [quoteLineData?.results, quoteDocument, event?.discount, event?.discountcost, proposalStatus]);

  // Refresh handlers
  const refreshQuoteDocument = useCallback(async () => {
    try {
      await refetchQuoteDocument();
    } catch (err) {
      console.error('Failed to refresh quote document:', err);
      setError('Failed to refresh quote document');
    }
  }, [refetchQuoteDocument]);

  const refreshQuoteLines = useCallback(async () => {
    try {
      await refetchQuoteLines();
    } catch (err) {
      console.error('Failed to refresh quote lines:', err);
      setError('Failed to refresh quote lines');
    }
  }, [refetchQuoteLines]);

  const refreshAll = useCallback(async () => {
    try {
      await Promise.all([refetchQuoteDocument(), refetchQuoteLines()]);
    } catch (err) {
      console.error('Failed to refresh proposal data:', err);
      setError('Failed to refresh proposal data');
    }
  }, [refetchQuoteDocument, refetchQuoteLines]);

  const contextValue: ProposalContextType = useMemo(
    () => ({
      proposalData,
      event,
      customer,
      property,
      quoteDocument: quoteDocument || null,
      isLoading:
        isQuoteDocumentLoading ||
        isQuoteLinesLoading ||
        quoteDocument == null ||
        createQuoteDocumentMutation.isPending,
      isQuoteDocumentLoading:
        isQuoteDocumentLoading || quoteDocument == null || createQuoteDocumentMutation.isPending,
      isQuoteLinesLoading,
      error,
      refreshQuoteDocument,
      refreshQuoteLines,
      refreshAll,
    }),
    [
      proposalData,
      event,
      customer,
      property,
      quoteDocument,
      isQuoteDocumentLoading,
      isQuoteLinesLoading,
      createQuoteDocumentMutation.isPending,
      error,
      refreshQuoteDocument,
      refreshQuoteLines,
      refreshAll,
    ],
  );

  return <ProposalContext.Provider value={contextValue}>{children}</ProposalContext.Provider>;
};
